<!--
 * @Author: Christion.Young
 * @Date: 2025-07-30 10:00:00
 * @LastEditors: Christion.Young
 * @LastEditTime: 2025-07-30 10:00:00
 * @Description: 登录弹窗组件 - 用于用户登录认证
 * @Features:
 *   - 支持用户名/手机号登录
 *   - 表单验证
 *   - 记住登录状态
 *   - 响应式设计
 *   - 登录成功后自动跳转
-->
<template>
  <div
    v-if="dialogVisible"
    class="login-dialog-overlay"
    @click.self="handleDialogClose"
  >
    <div class="login-dialog">
      <!-- 弹窗头部 -->
      <div class="dialog-header">
        <h3 class="dialog-title">用户登录</h3>
        <button class="close-btn" @click="handleDialogClose">
          <i class="el-icon-close"></i>
        </button>
      </div>

      <!-- 登录表单 -->
      <div class="dialog-body">
        <form @submit.prevent="handleLogin" class="login-form">
          <!-- 登录提示语 -->
          <div class="login-tip">
            <i class="el-icon-info"></i>
            <span
              >初始密码为"三火私服"小程序登录手机号，如忘记密码请发邮件申请重置初始密码，请妥善保管。</span
            >
          </div>

          <!-- 用户名/手机号输入框 -->
          <div class="form-item">
            <div class="input-wrapper">
              <i class="el-icon-user input-icon"></i>
              <input
                v-model="loginForm.username"
                type="text"
                placeholder="请输入用户名或手机号"
                class="form-input"
                @keyup.enter="handleLogin"
              />
            </div>
            <div v-if="errors.username" class="error-message">
              {{ errors.username }}
            </div>
          </div>
          <!-- 密码输入框 -->
          <div class="form-item">
            <div class="input-wrapper">
              <i class="el-icon-lock input-icon"></i>
              <input
                v-model="loginForm.password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="请输入密码"
                class="form-input"
                @keyup.enter="handleLogin"
              />
              <button
                type="button"
                class="password-toggle"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'el-icon-view' : 'el-icon-hide'"></i>
              </button>
            </div>
            <div v-if="errors.password" class="error-message">
              {{ errors.password }}
            </div>
          </div>

          <!-- 记住密码选项 -->
          <div class="form-item">
            <div class="login-options">
              <label class="checkbox-wrapper">
                <input
                  type="checkbox"
                  v-model="loginForm.rememberMe"
                  class="checkbox-input"
                />
                <span class="checkbox-label">记住登录状态</span>
              </label>
            </div>
          </div>

          <!-- 登录按钮 -->
          <div class="form-item">
            <button type="submit" class="login-btn" :disabled="loginLoading">
              {{ loginLoading ? "登录中..." : "登录" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "LoginDialog",
  data() {
    return {
      // 弹窗显示状态
      dialogVisible: false,
      // 登录加载状态
      loginLoading: false,
      // 登录成功后的重定向路径
      redirectPath: "/",
      // 密码显示状态
      showPassword: false,
      // 表单错误信息
      errors: {},

      // 登录表单数据
      loginForm: {
        username: "", // 用户名/手机号
        password: "", // 密码
        rememberMe: false, // 记住登录状态
      },
    };
  },
  created() {
    // 监听显示登录弹窗事件
    this.$bus.on("show-login-dialog", this.showDialog);
    // 监听ESC键关闭弹窗
    document.addEventListener("keydown", this.handleKeydown);
  },
  beforeDestroy() {
    // 移除事件监听
    this.$bus.off("show-login-dialog", this.showDialog);
    document.removeEventListener("keydown", this.handleKeydown);
  },
  methods: {
    /**
     * 处理键盘事件
     */
    handleKeydown(event) {
      if (event.key === "Escape" && this.dialogVisible) {
        this.handleDialogClose();
      }
    },

    /**
     * 显示登录弹窗
     * @param {Object} options - 配置选项
     * @param {string} options.redirectPath - 登录成功后的重定向路径
     */
    showDialog(options = {}) {
      this.dialogVisible = true;
      this.redirectPath = options.redirectPath || "/";
      this.resetForm();
    },

    /**
     * 隐藏登录弹窗
     */
    hideDialog() {
      this.dialogVisible = false;
    },

    /**
     * 处理弹窗关闭事件
     */
    handleDialogClose() {
      this.resetForm();
      this.loginLoading = false;
      this.hideDialog();
    },

    /**
     * 重置登录表单
     */
    resetForm() {
      // 重置表单数据
      this.loginForm = {
        username: "",
        password: "",
        rememberMe: false,
      };
      this.errors = {};
      this.showPassword = false;
    },

    /**
     * 验证表单
     */
    validateForm() {
      this.errors = {};

      if (!this.loginForm.username.trim()) {
        this.errors.username = "请输入用户名或手机号";
      } else if (this.loginForm.username.trim().length < 3) {
        this.errors.username = "用户名至少3个字符";
      }

      if (!this.loginForm.password) {
        this.errors.password = "请输入密码";
      } else if (this.loginForm.password.length < 6) {
        this.errors.password = "密码至少6个字符";
      }

      return Object.keys(this.errors).length === 0;
    },

    /**
     * 处理登录提交
     */
    async handleLogin() {
      // 表单验证
      if (!this.validateForm()) {
        return;
      }

      this.loginLoading = true;

      try {
        // 调用登录API
        const loginData = await this.performLogin();

        // 登录成功处理
        await this.handleLoginSuccess(loginData);
      } catch (error) {
        // 登录失败处理
        this.handleLoginError(error);
      } finally {
        this.loginLoading = false;
      }
    },

    /**
     * 执行登录API调用
     * @returns {Promise<Object>} 登录结果数据
     */
    async performLogin() {
      try {
        // 构造登录请求数据
        const loginData = {
          username: this.loginForm.username.trim(),
          password: this.loginForm.password,
          rememberMe: this.loginForm.rememberMe,
        };

        // 调用Vuex登录Action
        const response = await this.$store.dispatch("user/login", loginData);

        if (!response.success) {
          throw new Error(response.message);
        }

        return response;
      } catch (error) {
        throw error;
      }
    },

    /**
     * 处理登录成功
     * @param {Object} response - 登录响应数据
     */
    async handleLoginSuccess(response) {
      // 获取用户信息
      const userInfo = this.$store.state.user.userInfo;

      // 触发登录成功事件
      this.$bus.emit("login-success", {
        userInfo,
        redirectPath: this.redirectPath,
      });

      // 关闭弹窗
      this.hideDialog();

      // 强制重新检查登录状态
      this.$store.dispatch("user/checkLoginStatus");

      // 跳转到目标页面
      if (this.redirectPath && this.redirectPath !== this.$route.path) {
        this.$router.push(this.redirectPath);
      }

      // 强制组件重新渲染
      this.$nextTick(() => {
        // 登录状态更新完成
      });
    },

    /**
     * 处理登录错误
     * @param {Error} error - 错误对象
     */
    handleLoginError(error) {
      // 显示错误消息
      const errorMessage = error.message || "登录失败，请重试";
      this.showMessage(errorMessage, "error");

      // 如果是密码错误，清空密码框
      if (errorMessage.includes("密码")) {
        this.loginForm.password = "";
      }
    },

    /**
     * 显示消息提示
     * @param {string} message - 消息内容
     * @param {string} type - 消息类型
     */
    showMessage(message, type = "info") {
      // 使用统一消息提示工具
      switch (type) {
        case "success":
          this.$msg.success(message);
          break;
        case "warning":
          this.$msg.warning(message);
          break;
        case "error":
          this.$msg.error(message);
          break;
        case "info":
        default:
          this.$msg.info(message);
          break;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// 红色主题色彩定义
$primary-red: #e74c3c;
$primary-red-dark: #c0392b;

/* 弹窗遮罩层 */
.login-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 登录弹窗 */
.login-dialog {
  width: 400px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

/* 弹窗头部 */
.dialog-header {
  background: linear-gradient(135deg, $primary-red, lighten($primary-red, 10%));
  color: white;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .dialog-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }

  .close-btn {
    background: none;
    border: none;
    color: white;
    font-size: 18px;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: background 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
}

/* 弹窗主体 */
.dialog-body {
  padding: 30px 20px 20px;
}

/* 登录表单 */
.login-form {
  .form-item {
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .input-wrapper {
    position: relative;
    display: flex;
    align-items: center;

    .input-icon {
      position: absolute;
      left: 12px;
      color: #909399;
      font-size: 16px;
      z-index: 1;
    }

    .form-input {
      width: 100%;
      height: 44px;
      padding: 0 40px 0 40px;
      border: 1px solid #dcdfe6;
      border-radius: 6px;
      font-size: 14px;
      outline: none;
      transition: border-color 0.3s ease;

      &:focus {
        border-color: $primary-red;
      }

      &::placeholder {
        color: #c0c4cc;
      }
    }

    .password-toggle {
      position: absolute;
      right: 12px;
      background: none;
      border: none;
      color: #909399;
      cursor: pointer;
      font-size: 16px;
      padding: 4px;

      &:hover {
        color: $primary-red;
      }
    }
  }

  .error-message {
    margin-top: 4px;
    font-size: 12px;
    color: #f56c6c;
  }
}

/* 登录选项 */
.login-options {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .checkbox-wrapper {
    display: flex;
    align-items: center;
    cursor: pointer;

    .checkbox-input {
      margin-right: 8px;
      accent-color: $primary-red;
    }

    .checkbox-label {
      font-size: 14px;
      color: #666;
    }
  }
}

/* 登录提示语 */
.login-tip {
  background: linear-gradient(
    135deg,
    rgba(52, 152, 219, 0.1) 0%,
    rgba(41, 128, 185, 0.05) 100%
  );
  border: 1px solid rgba(52, 152, 219, 0.2);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
    border-radius: 8px 0 0 8px;
  }

  i {
    color: #3498db;
    font-size: 16px;
    margin-top: 2px;
    flex-shrink: 0;
  }

  span {
    color: #2c3e50;
    font-size: 13px;
    line-height: 1.5;
    font-weight: 500;
  }
}

/* 登录按钮 */
.login-btn {
  width: 100%;
  height: 44px;
  background: linear-gradient(135deg, $primary-red, lighten($primary-red, 5%));
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, $primary-red-dark, $primary-red);
    transform: translateY(-1px);
  }

  &:disabled {
    background: #c0c4cc;
    cursor: not-allowed;
    transform: none;
  }
}
</style>
